import { pluralize } from "@common/helpers/string";
import { Bill, BillLineItem } from "@common/types/bill";
import { BillLineItemView } from "@common/types/views/billViews";
import { LineItemsCommandIslandFooter } from "@components/lineItems/LineItemsCommandIslandFooter";
import React, { useMemo } from "react";
import { BillLineItemsView } from "@components/bills/views/billLineItemsView/BillLineItemsView";
import { BillScheduleWiring } from "@components/bills/schedules/billScheduleColumns";
import { useBillsView } from "../../../../contexts/BillsViewContext";
import { PaginatedBillsTableContext } from "../../PaginatedBillsTable.context";
import { useBillTableEmptyView } from "../../useBillTableEmptyView";

interface BillLineItemsViewContainerProps {
    view: BillLineItemView;
    hasAnyAccrualLedgers: boolean;
    onBillClick: (bill: Bill) => void;
    selectedBillId?: string;
    selectedLineItems?: Record<string, BillLineItem>;
    hasSelectedByCurrentFilters?: boolean;
    onSelectedLineItemsChange?: (lineItems: BillLineItem[]) => void;
    selectedCount: number;
    hasSelection: boolean;
    onClearSelection: () => void;
    onSelectAllByFilters: () => void;
    onOpenBulkActions: () => void;
    hasConnectedBillAccounts: boolean;
    onClearFilters: () => void;
    scheduleWiring?: BillScheduleWiring;
}

export const BillLineItemsViewContainer: React.FC<
    BillLineItemsViewContainerProps
> = ({
    view,
    hasAnyAccrualLedgers,
    onBillClick,
    selectedBillId,
    selectedLineItems,
    hasSelectedByCurrentFilters = false,
    onSelectedLineItemsChange,
    selectedCount,
    hasSelection,
    onClearSelection,
    onSelectAllByFilters,
    onOpenBulkActions,
    hasConnectedBillAccounts,
    onClearFilters,
    scheduleWiring,
}) => {
    const {
        bills,
        billCount,
        currentSort,
        handleSortChange,
        currentPage,
        totalPages,
        setPage,
        isLoading,
    } = React.useContext(PaginatedBillsTableContext);
    const { billStats } = useBillsView();

    const { emptyView, shouldHideFooter } = useBillTableEmptyView({
        isLoading,
        filteredCount: billCount,
        hasConnectedBillAccounts,
        onClearFilters,
        viewKind: "lineItems",
    });

    const stats = useMemo(() => {
        if (!billStats) {
            return undefined;
        }
        const totalOpenAmount = billStats.open.total;
        const totalPaidAmount = billStats.paid.total;
        return {
            totalOpenAmount,
            totalPaidAmount,
            totalAmount: totalOpenAmount + totalPaidAmount,
            count: billCount,
            countLabel: pluralize("Bill", billCount),
        };
    }, [billStats, billCount]);

    const footer = (
        <LineItemsCommandIslandFooter
            totalLabel={`${billCount} ${pluralize("bill", billCount)}`}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={setPage}
            selectedCount={selectedCount}
            hasSelection={hasSelection}
            hasSelectedByCurrentFilters={hasSelectedByCurrentFilters}
            onClearSelection={onClearSelection}
            onSelectAllByFilters={onSelectAllByFilters}
            onOpenBulkActions={onOpenBulkActions}
            stats={stats}
        />
    );

    return (
        <BillLineItemsView
            view={view}
            hasAnyAccrualLedgers={hasAnyAccrualLedgers}
            bills={bills ?? null}
            selectedBillId={selectedBillId}
            onBillClick={onBillClick}
            sort={currentSort}
            onSortChange={handleSortChange}
            footer={shouldHideFooter ? undefined : footer}
            selectedLineItems={selectedLineItems}
            hasSelectedByCurrentFilters={hasSelectedByCurrentFilters}
            onSelectedLineItemsChange={onSelectedLineItemsChange}
            emptyView={emptyView}
            scheduleWiring={scheduleWiring}
        />
    );
};
